/**
 * Centralized Route Definitions
 * Single source of truth for all locale-specific URLs
 *
 * Benefits:
 * - Type-safe route generation
 * - No duplicate URL logic across 45+ files
 * - Easy to maintain and update
 * - Prevents typos and inconsistencies
 *
 * Swedish (sv) slugs are ASCII-transliterations of native Swedish terms
 * (å→a, ä→a, ö→o) for compatibility with web servers, analytics
 * systems and to follow the convention used by Trivec / Foodtoday / Purspot.
 * The body content keeps native å/ä/ö where appropriate.
 */

import { defaultLocale, type Locale } from './i18n';

type LocaleMap = Partial<Record<Locale, string>> & { [k: string]: string | undefined };
export type RouteKey = keyof typeof ROUTES;

/**
 * All locale-specific routes in one place
 * Format: { en: '/english-url', nl: '/dutch-url', de: '/german-url', sv: '/swedish-url' }
 */
export const ROUTES = {
  // ===== SOLUTIONS =====
  kiosk: {
    en: '/self-ordering-kiosk',
    nl: '/bestelzuil',  // Updated from /horeca-kiosk (320/mo primary keyword)
    de: '/bestellterminal-gastronomie',
    sv: '/sjalvbetjaningskiosk',  // self-ordering kiosk (NOT POS); ASCII for självbetjäningskiosk
  },
  qr: {
    en: '/qr-ordering',
    nl: '/qr-bestellen',
    de: '/qr-bestellung',
    sv: '/qr-bestallning',  // ASCII for QR-beställning; Caspeco/Trivec standard
  },
  tablet: {
    en: '/tablet-ordering',
    nl: '/tablet-bestellen',
    de: '/tablet-bestellung',
    sv: '/tablet-bestallning',  // ASCII for tablet-beställning
  },
  webshop: {
    en: '/online-ordering-webshop',
    nl: '/online-bestellen-webshop',
    de: '/online-bestell-webshop',
    sv: '/onlinebestallning-webshop',  // ASCII for onlinebeställning (one word per SV convention)
  },
  ayce: {
    en: '/all-you-can-eat',
    nl: '/all-you-can-eat',
    de: '/all-you-can-eat',
    sv: '/all-you-can-eat',  // Anglicism kept (matches NL/DE convention)
  },

  // ===== GUIDE PAGES =====
  posGuide: {
    en: '/pos-system-hospitality',
    nl: '/kassasysteem-horeca',
    de: '/kassensystem-gastronomie',
    sv: '/kassasystem-restaurang',  // Guide ABOUT POS for restaurants (informational, not Jamezz product)
  },
  staffShortage: {
    en: '/staff-shortage-hospitality',
    nl: '/personeelstekort-horeca',
    de: '/personalmangel-gastronomie',
    sv: '/personalbrist-restaurang',  // personalbrist = staff shortage in Swedish
  },
  // ===== CORE PAGES =====
  faq: {
    en: '/faq',
    nl: '/faq',
    de: '/faq',
    sv: '/faq',
  },
  home: {
    en: '/',
    nl: '/',
    de: '/',
    sv: '/',
  },
  demo: {
    en: '/request-demo',
    nl: '/demo-aanvragen',
    de: '/demo-anfragen',
    sv: '/boka-demo',  // "Book demo" — natural Swedish CTA, used by all major Swedish SaaS vendors
  },
  contact: {
    en: '/contact',
    nl: '/contact',
    de: '/kontakt',
    sv: '/kontakt',  // Same as German; standard Swedish spelling
  },
  thankYou: {
    en: '/thank-you',
    nl: '/bedankt',
    de: '/danke',
    sv: '/tack',  // Standard Swedish
  },
  leadRegistration: {
    en: '/lead-registration',
    nl: '/lead-registratie',
    de: '/lead-registrierung',
    sv: '/registrera-lead',  // imperative form, more action-oriented in Swedish UX
  },
  leadRegistered: {
    en: '/lead-registered',
    nl: '/lead-geregistreerd',
    de: '/lead-registriert',
    sv: '/lead-registrerad',
  },
  reviews: {
    en: '/reviews',
    nl: '/beoordelingen',
    de: '/bewertungen',
    sv: '/recensioner',  // Standard Swedish term for reviews
  },

  // ===== COLLECTIONS =====
  integrations: {
    en: '/integrations',
    nl: '/integraties',
    de: '/integrationen',
    sv: '/integrationer',  // Swedish plural form
  },
  industries: {
    en: '/industries',
    nl: '/branches',
    de: '/branchen',
    sv: '/branscher',  // locked in plan; matches folder name in locales/sv/
  },
  blog: {
    en: '/blog',
    nl: '/blog',
    de: '/blog',
    sv: '/blogg',  // Swedish spelling (double g) — consistent with Caspeco, Karma, Trivec
  },

  // ===== LEGAL PAGES =====
  privacy: {
    en: '/privacy-policy',
    nl: '/privacy-verklaring',
    de: '/datenschutz',
    sv: '/integritetspolicy',  // GDPR standard Swedish term
  },
  terms: {
    en: '/terms-and-conditions',
    nl: '/algemene-voorwaarden',
    de: '/allgemeine-geschaeftsbedingungen',
    sv: '/anvandarvillkor',  // ASCII for användarvillkor; standard SV legal term
  },
  cookies: {
    en: '/cookie-policy',
    nl: '/cookiebeleid',
    de: '/cookie-richtlinie',
    sv: '/cookiepolicy',  // One word, Swedish convention
  },

  // ===== INDUSTRY PAGES =====
  // Each industry has locale-specific slugs.
  // Swedish slugs use validated branche-terminology from docs/sv-seo-keywords.md (sectie 0bis).
  'industry-airports': {
    en: '/industries/airports',
    nl: '/branches/luchthavens',
    de: '/branchen/flughaefen',
    sv: '/branscher/flygplatser',
  },
  'industry-all-you-can-eat': {
    en: '/industries/all-you-can-eat',
    nl: '/branches/all-you-can-eat',
    de: '/branchen/all-you-can-eat',
    sv: '/branscher/all-you-can-eat',
  },
  'industry-amusement-parks': {
    en: '/industries/amusement-parks',
    nl: '/branches/pretparken',
    de: '/branchen/freizeitparks',
    sv: '/branscher/nojesparker',  // ASCII for nöjesparker
  },
  'industry-bakeries': {
    en: '/industries/bakeries',
    nl: '/branches/bakkerijen',
    de: '/branchen/baeckereien',
    sv: '/branscher/bagerier',
  },
  'industry-beach-clubs': {
    en: '/industries/beach-clubs',
    nl: '/branches/strandtenten',
    de: '/branchen/strandclubs',
    sv: '/branscher/strandbarer',  // strandbar plural (Ancon-validated for beach venues)
  },
  'industry-bowling-centers': {
    en: '/industries/bowling-centers',
    nl: '/branches/bowlingcentra',
    de: '/branchen/bowlingcenter',
    sv: '/branscher/bowlinghallar',  // Anolla/Booklux-validated SV term
  },
  'industry-cafes-bars': {
    en: '/industries/cafes-bars',
    nl: '/branches/cafes-bars',
    de: '/branchen/cafes-bars',
    sv: '/branscher/kafeer-och-barer',  // ASCII for kaféer-och-barer
  },
  'industry-canteens': {
    en: '/industries/canteens',
    nl: '/branches/kantines',
    de: '/branchen/kantinen',
    sv: '/branscher/personalmatsalar',  // canteen = personalmatsal in Swedish
  },
  'industry-casinos': {
    en: '/industries/casinos',
    nl: '/branches/casinos',
    de: '/branchen/kasinos',
    sv: '/branscher/kasinon',  // Swedish plural of kasino
  },
  'industry-catering': {
    en: '/industries/catering',
    nl: '/branches/catering',
    de: '/branchen/catering',
    sv: '/branscher/catering',  // Anglicism widely used
  },
  'industry-concert-halls': {
    en: '/industries/concert-halls',
    nl: '/branches/concertzalen',
    de: '/branchen/konzertsaele',
    sv: '/branscher/konsertlokaler',
  },
  'industry-congress-centers': {
    en: '/industries/congress-centers',
    nl: '/branches/congressen',
    de: '/branchen/kongresse',
    sv: '/branscher/kongresscenter',
  },
  'industry-cruise-ships': {
    en: '/industries/cruise-ships',
    nl: '/branches/cruiseschepen',
    de: '/branchen/kreuzfahrtschiffe',
    sv: '/branscher/kryssningsfartyg',
  },
  'industry-events-festivals': {
    en: '/industries/events-festivals',
    nl: '/branches/events-festivals',
    de: '/branchen/events-festivals',
    sv: '/branscher/evenemang-och-festivaler',
  },
  'industry-expos': {
    en: '/industries/expos',
    nl: '/branches/beurzen',
    de: '/branchen/messen',
    sv: '/branscher/massor',  // ASCII for mässor
  },
  'industry-fast-food': {
    en: '/industries/fast-food',
    nl: '/branches/fastfood',
    de: '/branchen/fastfood',
    sv: '/branscher/snabbmat',  // QSR — gets MAX-Burgers-style "expresskiosk" body content
  },
  'industry-ferries': {
    en: '/industries/ferries',
    nl: '/branches/ferries',
    de: '/branchen/faehren',
    sv: '/branscher/farjor',  // ASCII for färjor
  },
  'industry-food-halls': {
    en: '/industries/food-halls',
    nl: '/branches/foodhalls',
    de: '/branchen/food-courts',
    sv: '/branscher/food-courts',  // Anglicism (same convention as DE)
  },
  'industry-food-trucks': {
    en: '/industries/food-trucks',
    nl: '/branches/foodtrucks',
    de: '/branchen/foodtrucks',
    sv: '/branscher/foodtrucks',  // Anglicism widely used in Swedish street-food scene
  },
  'industry-golf-courses': {
    en: '/industries/golf-courses',
    nl: '/branches/golfbanen',
    de: '/branchen/golfbahnen',
    sv: '/branscher/golfklubbar',  // Crona-validated; 250+ Swedish golf clubs use this term
  },
  'industry-holiday-parks': {
    en: '/industries/holiday-parks',
    nl: '/branches/vakantieparken',
    de: '/branchen/ferienparks',
    sv: '/branscher/semesterparker',
  },
  'industry-hospitals': {
    en: '/industries/hospitals',
    nl: '/branches/ziekenhuizen',
    de: '/branchen/krankenhaeuser',
    sv: '/branscher/sjukhus',
  },
  'industry-hotels': {
    en: '/industries/hotels',
    nl: '/branches/hotels',
    de: '/branchen/hotels',
    sv: '/branscher/hotell',  // Swedish spelling (single l, not double)
  },
  'industry-ice-cream-parlors': {
    en: '/industries/ice-cream-parlors',
    nl: '/branches/ijssalons',
    de: '/branchen/eisdielen',
    sv: '/branscher/glassbarer',  // glassbar is the Swedish term for ice cream parlor
  },
  'industry-lunchrooms': {
    en: '/industries/lunchrooms',
    nl: '/branches/lunchrooms',
    de: '/branchen/lunchrooms',
    sv: '/branscher/lunchrestauranger',  // Standard Swedish term
  },
  'industry-museums': {
    en: '/industries/museums',
    nl: '/branches/musea',
    de: '/branchen/museen',
    sv: '/branscher/museer',
  },
  'industry-restaurants': {
    en: '/industries/restaurants',
    nl: '/branches/restaurants',
    de: '/branchen/restaurants',
    sv: '/branscher/restauranger',  // Swedish plural of restaurang
  },
  'industry-spa-wellness': {
    en: '/industries/spa-wellness',
    nl: '/branches/spa-wellness',
    de: '/branchen/spa-wellness',
    sv: '/branscher/spa-och-wellness',
  },
  'industry-stadiums': {
    en: '/industries/stadiums',
    nl: '/branches/stadions',
    de: '/branchen/stadien',
    sv: '/branscher/arenor',  // arena/arenor is the dominant SV term (Friends Arena, Tele2 Arena, etc.)
  },
  'industry-universities': {
    en: '/industries/universities',
    nl: '/branches/universiteiten',
    de: '/branchen/universitaeten',
    sv: '/branscher/universitet',  // singular form serves as plural in SV (universitet/universitet)
  },
  'industry-zoos': {
    en: '/industries/zoos',
    nl: '/branches/dierentuinen',
    de: '/branchen/zoos',
    sv: '/branscher/djurparker',  // standard Swedish term for zoo
  },
} as const;

/**
 * Safe route getter for new locales.
 * If a route isn't defined for a locale yet, we fall back to defaultLocale (en).
 */
export function routeFor(routeKey: RouteKey, locale: Locale): string {
  const map = ROUTES[routeKey] as unknown as LocaleMap;
  const v = map?.[locale];
  if (typeof v === 'string' && v.length) return v;
  const fallback = map?.[defaultLocale];
  if (typeof fallback === 'string' && fallback.length) return fallback;
  // last resort: try 'en'
  const en = map?.['en'];
  return typeof en === 'string' && en.length ? en : '/';
}

/**
 * Type-safe route getter
 * @param route - Route key from ROUTES object
 * @param locale - Current locale (en, nl, de, sv)
 * @returns Localized path (e.g., '/bestelzuil' for kiosk + nl)
 *
 * @example
 * getRoute('kiosk', 'nl') → '/bestelzuil'
 * getRoute('qr', 'en') → '/qr-ordering'
 * getRoute('kiosk', 'sv') → '/sjalvbetjaningskiosk'
 */
export function getRoute(route: keyof typeof ROUTES, locale: Locale): string {
  // Fallback: if a new locale is partially configured, use the defaultLocale path
  // so pages still render (SEO slugs can be refined later).
  return (ROUTES[route] as any)?.[locale] ?? (ROUTES[route] as any)?.[defaultLocale] ?? '/';
}

/**
 * Get integration detail page path
 * @param slug - Integration slug (e.g., 'lightspeed')
 * @param locale - Current locale
 * @returns Full path (e.g., '/nl/integraties/lightspeed')
 */
export function getIntegrationPath(slug: string, locale: Locale): string {
  const basePath = locale === 'en' ? '' : `/${locale}`;
  return `${basePath}${ROUTES.integrations[locale]}/${slug}`;
}

/**
 * Get partner detail page path
 * @param slug - Partner slug (e.g., 'konacom')
 * @param locale - Current locale
 * @returns Full path (e.g., '/nl/partners/konacom')
 */
export function getPartnerPath(slug: string, locale: Locale): string {
  // Partners don't have translated paths - always use "partners"
  const basePath = locale === 'en' ? '' : `/${locale}`;
  return `${basePath}/partners/${slug}`;
}

/**
 * Get integration or partner path based on slug
 * Automatically determines if it's a partner or integration
 * @param slug - Integration/partner slug
 * @param locale - Current locale
 * @returns Full path to integration or partner page
 */
export function getIntegrationOrPartnerPath(slug: string, locale: Locale): string {
  // List of partners that should go to /partners/ instead of /integrations/
  const partners = [
    'konacom', 'possystems', 'cecon', 'janssens-kassasystemen', 'ms-pos',
    'adnamics', 'upta', 'kassanet-pieterse', 'opsomer', 'evers-kassasystemen',
    'stn', 'kassanet-nuus', 'bork', 'prestop', 'acrelec', 'qiox',
    'van-hessen'
  ];

  const isPartner = partners.includes(slug);
  return isPartner ? getPartnerPath(slug, locale) : getIntegrationPath(slug, locale);
}

/**
 * Get industry/branch detail page path
 * @param slug - Industry slug (e.g., 'hotels')
 * @param locale - Current locale
 * @returns Full path (e.g., '/branches/hotels')
 */
export function getIndustryPath(slug: string, locale: Locale): string {
  return `${ROUTES.industries[locale]}/${slug}`;
}

/**
 * Get blog post path
 * @param slug - Blog post slug
 * @param locale - Current locale
 * @returns Full path (e.g., '/blog/post-slug')
 */
export function getBlogPath(slug: string, locale: Locale): string {
  return `${ROUTES.blog[locale]}/${slug}`;
}

/**
 * Get a fully-prefixed localized URL for a route (slug + /<locale> prefix).
 *
 * @example
 *   getLocalizedRoute('industries', 'en') → '/industries'
 *   getLocalizedRoute('industries', 'nl') → '/nl/branches'
 *   getLocalizedRoute('industries', 'sv') → '/sv/branscher'
 *
 * Use deze helper i.p.v. inline `locale === 'nl' ? ... : locale === 'de' ? ...`
 * ternaries (geen SV-fallback nodig — automatisch correct voor nieuwe locales).
 */
export function getLocalizedRoute(routeKey: keyof typeof ROUTES, locale: Locale): string {
  const slug = getRoute(routeKey, locale);
  if (locale === defaultLocale) return slug;
  return `/${locale}${slug}`;
}

/**
 * Shorthand helpers for commonly used routes
 */
export const getKioskPath = (locale: Locale) => getRoute('kiosk', locale);
export const getQrPath = (locale: Locale) => getRoute('qr', locale);
export const getTabletPath = (locale: Locale) => getRoute('tablet', locale);
export const getWebshopPath = (locale: Locale) => getRoute('webshop', locale);
export const getDemoPath = (locale: Locale) => getRoute('demo', locale);
export const getContactPath = (locale: Locale) => getRoute('contact', locale);
export const getReviewsPath = (locale: Locale) => getRoute('reviews', locale);

/**
 * Get industry route key from English slug
 * @param slug - English industry slug (e.g., 'restaurants', 'hotels')
 * @returns Route key (e.g., 'industry-restaurants')
 */
export function getIndustryRouteKey(slug: string): keyof typeof ROUTES | null {
  const key = `industry-${slug}` as keyof typeof ROUTES;
  if (key in ROUTES) {
    return key;
  }
  return null;
}

/**
 * Get full industry path with correct locale-specific slug
 * @param slug - English industry slug
 * @param locale - Current locale
 * @returns Full localized path (e.g., '/branches/restaurants' for NL, '/branscher/restauranger' for SV)
 */
export function getLocalizedIndustryPath(slug: string, locale: Locale): string {
  const routeKey = getIndustryRouteKey(slug);
  if (routeKey && ROUTES[routeKey]) {
    return ROUTES[routeKey][locale];
  }
  // Fallback to generic path
  return `${ROUTES.industries[locale]}/${slug}`;
}
